// covered-by: frontends/carrie/src/ask-v2/conversation/__tests__/useReadingMessage.test.tsx
import { type RefObject, useEffect, useLayoutEffect, useState } from "react";

/** How far down the thread (0 = top, 1 = bottom) the reader's eye is taken to be. */
export const READING_LINE = 0.35;

/** Within this many px of the end, the reader is on the latest answer. */
export const AT_BOTTOM_PX = 48;

const ANSWER_SELECTOR = '[data-message-role="assistant"][data-message-id]';

/**
 * The assistant answer the reader is on: the last one whose top has scrolled
 * above the reading line. Null at the bottom of the thread, where the reader
 * is on the latest answer whatever its length.
 */
export function readingMessageId(container: HTMLElement): string | null {
  const fromEnd =
    container.scrollHeight - container.scrollTop - container.clientHeight;
  if (fromEnd <= AT_BOTTOM_PX) return null;
  const box = container.getBoundingClientRect();
  const line = box.top + box.height * READING_LINE;
  const answers = container.querySelectorAll<HTMLElement>(ANSWER_SELECTOR);
  // why: scan from the end -- the reader is usually just above the bottom
  // cutoff, where the old forward scan walked every answer and this walks one.
  for (let i = answers.length - 1; i >= 0; i -= 1) {
    if (answers[i].getBoundingClientRect().top <= line) {
      return answers[i].dataset.messageId ?? null;
    }
  }
  return null;
}

/**
 * Tell `onChange` which answer the reader scrolls to, once per change.
 *
 * The v2 rail shows the citations of that answer instead of always the latest
 * one, so a reader going back through a conversation sees what each answer
 * stood on.
 */
export function useReadingMessage(
  containerRef: RefObject<HTMLElement | null>,
  onChange: ((messageId: string | null) => void) | undefined,
) {
  const [root, setRoot] = useState<HTMLElement | null>(null);
  // why-not: an effect keyed on the ref object misses the container mounting
  // after the first render (the review workspace swaps it out and back).
  useLayoutEffect(() => {
    setRoot((previous) => {
      const next = containerRef.current;
      return previous === next ? previous : next;
    });
  });

  useEffect(() => {
    if (!root || !onChange) return;
    let frame = 0;
    let last: string | null | undefined;
    const report = () => {
      frame = 0;
      const next = readingMessageId(root);
      if (next !== last) {
        last = next;
        onChange(next);
      }
    };
    const onScroll = () => {
      if (frame === 0) frame = requestAnimationFrame(report);
    };
    root.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      root.removeEventListener("scroll", onScroll);
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, [root, onChange]);
}
